import React, { createContext, useContext } from "react";
import type { EditorDraft, EditorState, EditorAction } from "@/lib/editor/editor-types";
import type { AutosaveStatus } from "@/lib/editor/use-autosave";
import type { ThreeWayRebaseResult } from "@/lib/editor/three-way-rebase";
import type { SectionType } from "@profolio/validators";
import type { ProfileData } from "@profolio/templates";
import type { SelectedNode, InspectorTab } from "@/lib/editor/types";

export type WorkspaceTab = "home" | "content" | "design" | "pages" | "publish";
export type PublishStatus = "draft_saved" | "publish_pending" | "publishing" | "published" | "error";

export interface PendingNavigation {
  destination: string | WorkspaceTab;
  destinationType: "workspace" | "route" | "external";
  source?: string;
}

export interface ProfileDraftContextValue {
  // Committed draft state
  serverSnapshot: EditorDraft | null;
  lastSavedDraft: EditorDraft | null;
  draft: EditorDraft | null;
  editorState: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  autosaveStatus: AutosaveStatus;
  publishStatus: PublishStatus;
  activeTab: WorkspaceTab;
  setActiveTab: (tab: WorkspaceTab) => void;
  activeSectionId: string | null;
  setActiveSectionId: (id: string | null) => void;
  previewOpen: boolean;
  setPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  previewViewport: "desktop" | "tablet" | "mobile";
  setPreviewViewport: (viewport: "desktop" | "tablet" | "mobile") => void;
  userTier: import("@profolio/validators").ProductTier;
  flushAutosave: () => Promise<void>;
  handleSaveDraft: () => Promise<void>;
  isSavingDraft: boolean;
  handlePublish: () => Promise<void>;
  isPublishing: boolean;
  publishedUrl: string;
  displayDomain: string;
  isDirty: boolean;
  isDesignDirty: boolean;
  isContentDirty: boolean;
  hasUnsavedChanges?: boolean;
  hasUnpublishedChanges?: boolean;
  isPublished?: boolean;
  onNavigateToSection: (sectionType: SectionType) => void;
  navigateToSectionDesign: (sectionType: string) => void;
  activeDesignSectionType: string | null;
  setActiveDesignSectionType: (sectionType: string | null) => void;
  onDiscardDraft: () => Promise<void>;

  // Centralized Dual-Rail & Section Navigation State
  isPrimaryRailExpanded: boolean;
  setIsPrimaryRailExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isContentNavCollapsed: boolean;
  setIsContentNavCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  isAppearanceOverlayOpen: boolean;
  setIsAppearanceOverlayOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSectionId: string | null;
  setSelectedSectionId: (id: string | null) => void;

  // New Shell State
  selectedNode: SelectedNode | null;
  setSelectedNode: (node: SelectedNode | null) => void;
  inspectorTab: InspectorTab;
  setInspectorTab: (tab: InspectorTab) => void;

  // Transient Design State (Quality Bar Pillar 5)
  designPreviewOverride: Partial<ProfileData> | null;
  setDesignPreviewOverride: (override: Partial<ProfileData> | null) => void;
  transientSectionOverrides: Record<string, any>;
  setTransientSectionOverrides: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  effectivePreviewDraft: EditorDraft | null;
  applyDesignChanges: () => void;
  discardDesignChanges: () => void;
  hasTransientDesignChanges: boolean;
  onUpgradeClick: (featureKey?: string) => void;

  // Universal Navigation Guard State (Phase A)
  pendingNavigation: PendingNavigation | null;
  isNavigationGuardOpen: boolean;
  isApplyingAndNavigating: boolean;
  requestNavigation: (nav: PendingNavigation) => void;
  confirmApplyAndNavigate: () => Promise<void>;
  confirmDiscardAndNavigate: () => void;
  cancelNavigation: () => void;

  // Conflict Resolution (Phase B)
  conflictResult: ThreeWayRebaseResult | null;
  isConflictModalOpen: boolean;
  setIsConflictModalOpen: (open: boolean) => void;
  resolveConflictKeepLocal: () => Promise<void>;
  resolveConflictUseServer: () => Promise<void>;
}

const ProfileDraftContext = createContext<ProfileDraftContextValue | null>(null);

/**
 * Raw context export for components that need to optionally read editor state
 * without throwing when outside the ProfileDraftProvider boundary (e.g. UnifiedHeader).
 */
export { ProfileDraftContext };

export function useProfileDraft(): ProfileDraftContextValue {
  const context = useContext(ProfileDraftContext);
  if (!context) {
    throw new Error("useProfileDraft must be used within a ProfileDraftProvider");
  }
  return context;
}

export function ProfileDraftProvider({
  value,
  children,
}: {
  value: ProfileDraftContextValue;
  children: React.ReactNode;
}) {
  return (
    <ProfileDraftContext.Provider value={value}>
      {children}
    </ProfileDraftContext.Provider>
  );
}
